import type { StarterOption } from '../../shared/protocol.ts';
import { $, el } from '../common/dom.ts';
import { typeBadges } from '../common/element-badge.ts';
import { rarityBadge } from '../common/rarity-badge.ts';
import type { SoundBoard } from './sound.ts';
import { creatureText } from '../../shared/game-config.ts';

/**
 * Premier passage : choix du monstre de départ parmi ceux définis dans l'administration.
 * La fenêtre ne se ferme qu'une fois le choix confirmé (flèches + Entrée au clavier).
 */
export class StarterPicker {
  private readonly root = $('#starter');
  private readonly cards = $('.starter-cards', this.root);
  private readonly confirm = $<HTMLButtonElement>('.starter-confirm', this.root);
  private readonly onPick: (speciesId: number) => void;
  private readonly sounds: SoundBoard;
  private options: StarterOption[] = [];
  private selected = -1;

  constructor(onPick: (speciesId: number) => void, sounds: SoundBoard) {
    this.onPick = onPick;
    this.sounds = sounds;
    this.confirm.addEventListener('click', () => this.pick());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  show(options: StarterOption[]): void {
    this.options = options;
    this.selected = -1;
    this.cards.replaceChildren(...options.map((option, index) => this.card(option, index)));
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  handleKey(event: KeyboardEvent): void {
    const step = { ArrowLeft: -1, ArrowUp: -1, KeyA: -1, KeyQ: -1, ArrowRight: 1, ArrowDown: 1, KeyD: 1 }[event.code];
    if (step !== undefined) {
      event.preventDefault();
      const count = this.options.length;
      this.select(this.selected < 0 ? (step > 0 ? 0 : count - 1) : (this.selected + step + count) % count);
    } else if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.pick();
    }
  }

  private card(option: StarterOption, index: number): HTMLElement {
    const { maxHp, atk, def, spd } = option.stats;
    const card = el('button', { type: 'button', className: 'starter-card' },
      el('img', { src: option.image, alt: option.name, className: 'pixel' }),
      el('strong', { textContent: option.name }),
      el('div', { className: 'badges' }, ...typeBadges(option), rarityBadge(option.rarity)),
      el('p', { textContent: creatureText(option.description) || 'Un compagnon plein de promesses.' }),
      el('small', { className: 'muted', textContent: `Nv ${option.level} · ${maxHp} PV · ATQ ${atk} · DÉF ${def} · VIT ${spd} (+ ses bonus, tirés au hasard)` }));
    card.addEventListener('click', () => this.select(index));
    card.addEventListener('dblclick', () => this.pick());
    return card;
  }

  private select(index: number): void {
    this.selected = index;
    this.sounds.play('select');
    this.render();
  }

  private render(): void {
    [...this.cards.children].forEach((card, index) => card.classList.toggle('selected', index === this.selected));
    const option = this.options[this.selected];
    this.confirm.disabled = !option;
    this.confirm.textContent = option ? `Choisir ${option.name}` : creatureText('Choisissez {un monstre}');
  }

  private pick(): void {
    const option = this.options[this.selected];
    if (!option) return;
    this.onPick(option.id);
    this.sounds.play('capture');
    this.close();
  }
}
